import constants from 'app/constants';
import { useAuthSelector } from 'app/data/auth';
import {
  Button,
  Dialog,
  DialogAlert,
  DialogLoading,
  DialogProps,
} from 'app/design-lib';
import { DialogActions } from 'app/design-lib/components/DialogActions';
import { DialogContent } from 'app/design-lib/components/DialogContent';
import { DialogHeader } from 'app/design-lib/components/DialogHeader';
import { useAccountStripeCustomerQuery } from 'app/hooks/queries/account';
import { gql, request } from 'graphql-request';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';

interface CancelSubDialogProps extends Omit<DialogProps, 'children'> {}

export const CancelSubDialog = (props: CancelSubDialogProps) => {
  return (
    <Dialog {...props}>
      {' '}
      <CancelSubDialogContent {...props} />
    </Dialog>
  );
};

const CancelSubDialogContent = (props: CancelSubDialogProps) => {
  const { data } = useAccountStripeCustomerQuery();

  const sub = data?.data?.subscriptions?.data?.[0];
  const { status, current_period_end, trial_end } = sub ?? {};

  const { auth_token } = useAuthSelector();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const router = useHistory();
  // though this is undoubtedly not a success that we are losing a customer,
  // the authors of this code agreed that for the sake consistency
  // across the app, the terminology should remain the same.
  const [isSuccess, setIsSuccess] = useState(false);
  const cancelSub = async () => {
    setIsSubmitting(true);
    try {
      const cancelResp = await request(
        `${constants.env.REACT_APP_CIO_API_SERVER}/api/graphql`,
        gql`
          mutation mutateAccount($data: JSON) {
            mutateAccount(data: $data) {
              success
              message
              data
            }
          }
        `,
        {
          data: {
            action: 'deleteAccount',
            data: {},
          },
        },
        // @ts-ignore
        { authorization: auth_token },
      );
      console.log('cancel response', cancelResp);
      setIsSuccess(true);

      // setTimeout(() => {
      //   router.push(`/logout`);
      // }, 5000);
    } catch (err) {
      console.error(err);
      setErrorMessage('Error in canceling subscription');
    }
    setIsSubmitting(false);
  };

  const cancelDate = new Date(current_period_end * 1000);
  const isTrial = status === 'trialing';
  return (
    <>
      <DialogHeader
        title={
          isSuccess
            ? 'Subscription canceled'
            : isSubmitting
            ? 'Canceling subscription'
            : `Cancel subscription ${
                isTrial ? 'at end of trial' : 'at end of billing cycle'
              }`
        }
        onClose={props.onClose}
        subTitle={isSubmitting ? 'Please wait...' : ''}
      />
      {!isSubmitting && errorMessage ? (
        <DialogAlert
          onClose={() => setErrorMessage('')}
          errorMessage={errorMessage}
        />
      ) : null}
      <DialogContent>
        {isSuccess ? (
          <div className={'w-full text-md text-center'}>
            <p>We're sorry to see you go.</p>
            <p>
              You may continue to use your account until{' '}
              {cancelDate.toLocaleDateString()}
            </p>
          </div>
        ) : isSubmitting ? (
          <DialogLoading
            label={'Canceling your subscription.'}
            secondaryText={'This may take a moment.'}
          />
        ) : (
          <div className={'text-center '}>
            <p className={'text-md'}>
              Are you sure you would like to cancel your subscription and delete
              your account?
            </p>
            <br />
            <p className={'text-md'}>
              {status === 'trialing'
                ? 'Once your free trial'
                : 'Once your current billing cycle'}{' '}
              expires{' '}
              {current_period_end ? (
                <>on {cancelDate.toLocaleDateString()} </>
              ) : null}
              <span className={'font-medium'}>
                your account will be deleted and you will lose all your data
                including your account's phone numbers and messages. All team
                members' data will be deleted too.
              </span>
              .
            </p>
          </div>
        )}
      </DialogContent>
      {isSubmitting ? null : (
        <DialogActions>
          {isSuccess ? (
            <Button
              variant={'fill'}
              color={'accent'}
              size={'md'}
              onClick={props.onClose}
            >
              Dismiss
            </Button>
          ) : (
            <Button
              variant={'fill'}
              color={'negative'}
              size={'md'}
              onClick={cancelSub}
            >
              Yes, Delete My Account
            </Button>
          )}
        </DialogActions>
      )}
    </>
  );
};

export default CancelSubDialog;
